import React, { Fragment } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { FormattedMessage } from 'react-intl';
import { Form, Header, Segment, Button, Icon, Loader } from 'semantic-ui-react';

import LeaseForm from './LeaseForm';
import LeasesTable from './LeasesTable';
import LeasesDetails from './LeasesDetails';
import PaginationSegment from '../util/PaginationSegment';
import { fetchActiveRole, fetchUserOrganizationalUnit } from '../util/roles';
import { IconHeader, FiltersOnStyle } from '../util/css';
import {
    getTotalNumberOfPages,
    checkOrganizationalUnitsPrivilege,
    checkWritePrivilege,
    formatListForDropdownOptions,
    DEFAULT_NUMBER_OF_ITEMS_PER_PAGE, getLeaseholderName
} from '../util/util';
import { fetchFaculties } from '../../Lookups/util';
import ReactExport from 'react-data-export';

const { ExcelFile } = ReactExport;
const { ExcelSheet } = ReactExport.ExcelFile;

const sanitize = (val) => {
    if (val == null) return '';
    if (val === '-') return '';
    if (typeof val === 'boolean') return val ? 'Da' : 'Ne';
    return String(val);
};

const LEASE_COLUMNS = [
    { key: 'leaseholderName', displayName: 'Zakupac', accessor: (el) => el.leaseholder ? getLeaseholderName(el.leaseholder) : '-' },
    { key: 'leaseSubject', displayName: 'Predmet zakupa', accessor: (el) => el.leaseSubject || '-' },
    { key: 'purpose', displayName: 'Namjena zakupa', accessor: (el) => el.purpose || '-' },
    { key: 'comment', displayName: 'Napomena', accessor: (el) => el.comment || '-' },
    { key: 'startDate', displayName: 'Početak zakupa', accessor: (el) => el.beginDate ? formatDate(el.beginDate) : '-' },
    { key: 'endDate', displayName: 'Istek zakupa', accessor: (el) => el.endDate ? formatDate(el.endDate) : '-' },
    { key: 'monthlyIncome', displayName: 'Mjesečni prihodi (BAM)', accessor: (el) => el.incomePerMonth },
];

const formatDate = (date) => {
    if (!date) return '-';
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}.${month}.${year}.`;
};

const LEASE_SEARCH_FIELDS = ['_searchLeaseholder', '_searchLeaseSubject', '_searchPurpose', '_searchComment'];

const addSearchFields = (items) => items.map(item => ({
    ...item,
    _searchLeaseholder: getLeaseholderName(item.leaseholder),
    _searchLeaseSubject: item.leaseSubject ? item.leaseSubject : '',
    _searchPurpose: item.purpose ? item.purpose : '',
    _searchComment: item.comment ? item.comment : '',
}));

const prepareExcelData = (leases) => [{
    columns: LEASE_COLUMNS.map(col => ({
        title: col.displayName,
        width: { wpx: 200 }
    })),
    data: leases.map(item =>
        LEASE_COLUMNS.map(col => ({
            value: sanitize(col.accessor(item))
        }))
    )
}];

axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.withCredentials = true;

class Leases extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            organizationalUnits: [],
            organizationalUnitId: null,
            selectedOrganizationalUnit: null,
            activeRole: null,

            leases: [],
            allLeases: [],
            filteredLeases: [],

            legalDocumentTypes: [],
            leaseholders: [],
            properties: [],

            // Component behaviour
            requestMode: null,
            loading: true,
            startDatePicked: false,
            endDatePicked: false,
            leaseholderSelected: null,
            propertySelected: null,

            // For details
            details: false,
            leaseObject: null,

            // For pagination
            activePage: 1,
            totalPages: 1,
            resultsPerPage: DEFAULT_NUMBER_OF_ITEMS_PER_PAGE,
            filterRequestData: null,

            deletingLeaseRowId: null,
            deletingLeaseSaving: false,

            // For search and excel
            searchText: '',
            excelData: [],

            // For sorting, default values
            fieldName: null,
            fieldProperty: null,
            direction: null,
        };

        this.getUserData = this.getUserData.bind(this);
        this.fetchDataForRole = this.fetchDataForRole.bind(this);
        this.getLeases = this.getLeases.bind(this);

        this.activateEditRequest = this.activateEditRequest.bind(this);
        this.activateAddRequest = this.activateAddRequest.bind(this);
        this.closeForm = this.closeForm.bind(this);

        this.getDataToRender = this.getDataToRender.bind(this);

        this.switchToDetails = this.switchToDetails.bind(this);
        this.switchToTable = this.switchToTable.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
        this.handleResultsPerPageChange = this.handleResultsPerPageChange.bind(this);
        this.getSortedLeases = this.getSortedLeases.bind(this);
        this.onChangeOrganizationalUnit = this.onChangeOrganizationalUnit.bind(this);
    }

    componentDidMount() {
        this.getUserData();
    }

    getUserData() {
        axios.all([fetchActiveRole(), fetchUserOrganizationalUnit()])
            .then(axios.spread((activeRoleRes, userOrganizationalUnitRes) => {
                const activeRole = activeRoleRes.data;
                const organizationalUnitId = userOrganizationalUnitRes.data === '' ? null : userOrganizationalUnitRes.data;
                this.setState({
                    activeRole,
                    organizationalUnitId
                }, () => {
                    this.fetchDataForRole(activeRole, organizationalUnitId);
                });
            }))
            .catch(err => {
                this.setState({
                    error: true,
                    loading: false
                });
                // console.log('Failed to fetch data.');
            });
    }

    fetchDataForRole(activeRole, organizationalUnitId) {
        if (checkOrganizationalUnitsPrivilege(activeRole)) {
            fetchFaculties()
                .then(res => {
                        const organizationalUnits = formatListForDropdownOptions(res.data);
                        this.setState({
                            organizationalUnits: organizationalUnits,
                            loading: false,
                        });
                    }
                )
                .catch(err => {
                    this.setState({
                        loading: false
                    });
                    toast('Greška pri dobavljanju podataka!');
                });
        } else {
            this.getLeases();
        }
    }

    getLeases(page = 0, resultsPerPage = DEFAULT_NUMBER_OF_ITEMS_PER_PAGE, sortingDTO = null) {
        const {
            activeRole,
            fieldName,
            fieldProperty,
            direction
        } = this.state;
        const pagination = {
            page,
            resultsPerPage,
            fieldName: sortingDTO ? sortingDTO.fieldName : fieldName,
            fieldProperty: sortingDTO ? sortingDTO.fieldProperty : fieldProperty,
            order: sortingDTO ? sortingDTO.direction === 'ascending' : direction ? direction === 'ascending' : null,
        };

        // imovina admin and super admin can only fetch when ou is selected
        if (checkOrganizationalUnitsPrivilege() && !this.state.selectedOrganizationalUnit) return;

        const url = checkOrganizationalUnitsPrivilege()
            ? `/assets/leases/by-organizational-unit/${this.state.selectedOrganizationalUnit}`
            : '/assets/leases/paginated/';
        this.setState({
            loading: true,
        }, () => {
            axios.post(url, { ...pagination })
                .then(res => {
                    this.updateLeases(page, resultsPerPage, res, sortingDTO);
                })
                .catch(err => {
                    toast('Došlo je do greške!');
                });
        });
    }

    getSortedLeases(fieldName, fieldProperty = null) {
        if (checkOrganizationalUnitsPrivilege(this.state.activeRole) && !this.state.selectedOrganizationalUnit) return;
        const sortingDTO = {
            direction: (this.state.fieldName === fieldName ? (this.state.direction === 'ascending' ? 'descending' : 'ascending') : 'ascending'),
            fieldName,
            fieldProperty,
        };

            this.getLeases(this.state.activePage - 1, this.state.resultsPerPage, sortingDTO);
    }

    updateLeases(page, resultsPerPage, res, sortingDTO, filtersOn = false) {
        const leases = res.data.responseList.map(el => ({
            ...el, beginDate: new Date(el.dateStart), endDate: new Date(el.dateEnd),
        }));
        const withSearchFields = addSearchFields(leases);

        let totalPages = getTotalNumberOfPages(res.data.total, resultsPerPage);
        this.setState(prevState => ({
            leases: filtersOn ? [] : leases,
            allLeases: filtersOn ? [] : withSearchFields,
            filteredLeases: filtersOn ? withSearchFields : withSearchFields,
            excelData: prepareExcelData(withSearchFields),
            totalPages,
            activePage: page + 1,
            resultsPerPage,
            fieldName: sortingDTO ? sortingDTO.fieldName : prevState.fieldName,
            fieldProperty: sortingDTO ? sortingDTO.fieldProperty : prevState.fieldProperty,
            direction: sortingDTO ? (sortingDTO.fieldName === prevState.fieldName ? (prevState.direction === 'ascending' ? 'descending' : 'ascending') : 'ascending') : prevState.direction,
            loading: false,
        }));
    }

    getResults = (results) => {
        if (results.length === 0 && this.state.searchText.trim() === '') {
            const withSearchFields = addSearchFields(this.state.leases);
            this.setState({
                filteredLeases: withSearchFields,
                excelData: prepareExcelData(withSearchFields),
            });
        } else {
            this.setState({
                filteredLeases: results,
                excelData: prepareExcelData(results),
            });
        }
    };

    handleSearchTextChange = (text) => {
        this.setState({ searchText: text });
    };

    getDataToRender() {
        return this.state.filteredLeases;
    }

    onChangeOrganizationalUnit(selectedOrganizationalUnit) {
        this.setState({
            selectedOrganizationalUnit,
            loading: true
        }, () => {
            this.getLeases(0, this.state.resultsPerPage);
        });
    }

    activateEditRequest(el) {
        this.setState({
            requestMode: 'edit',
            leaseObject: el,
        });
    }

    activateAddRequest() {
        this.setState({
            requestMode: 'add',
            leaseObject: null,
        });
    }

    closeForm() {
        this.setState({
            requestMode: null,
            leaseObject: null
        });
    }

    switchToDetails(lease) {
        // console.log(lease);
        if (lease !== null) {
            this.setState({
                details: true,
                leaseObject: lease,
            });
        }
    }

    switchToTable() {
        this.setState({
            leaseObject: null,
            details: false,
        });
    }

    handlePageChange(e, { activePage }) {
        if (activePage === this.state.activePage) return;
        this.getLeases(activePage - 1, this.state.resultsPerPage);
    }

    handleResultsPerPageChange(e, { value }) {
        this.getLeases(0, value);
    }

    handleLeaseDeleteClick = (id) => {
        this.setState({ deletingLeaseRowId: id });
    }

    handleLeaseDeleteCancel = () => {
        this.setState({ deletingLeaseRowId: null });
    }

    handleLeaseDeleteConfirm = () => {
        const { deletingLeaseRowId } = this.state;
        this.setState({ deletingLeaseSaving: true });
        axios.delete(`/assets/leases/${deletingLeaseRowId}/`)
            .then(() => {
                toast('Uspješno brisanje ugovora!');
                this.setState({ deletingLeaseRowId: null, deletingLeaseSaving: false });
                this.getLeases(this.state.activePage - 1, this.state.resultsPerPage);
            })
            .catch(err => {
                const msg = err && err.response && err.response.data ? err.response.data : '';
                if (msg.includes('ConstraintViolationException')) {
                    toast(<FormattedMessage id="assets.leases.deleteConstraintError"/>);
                } else {
                    toast(<FormattedMessage id="assets.leases.deleteError"/>);
                }
                this.setState({ deletingLeaseRowId: null, deletingLeaseSaving: false });
            });
    }

    render() {
        const {
            details, leaseObject, fieldName, direction, activeRole,
        } = this.state;
        if (this.state.error) return <div>Greška</div>;
        const leases = this.getDataToRender();
        const organizationalUnitsPrivilege = checkOrganizationalUnitsPrivilege(activeRole);
        const writePrivilege = checkWritePrivilege(activeRole);
        const isFormActive = this.state.requestMode === 'edit' || this.state.requestMode === 'add';

        if (details) {
            return (<LeasesDetails
                lease={leaseObject}
                switchToTable={this.switchToTable}
            />);
        }

        return (
            <div>
                <Header as="h1">Zakupi</Header>
                <Segment.Group raised>
                    <Segment>
                        <Header as="h3">
                        Spisak zakupa

                            <Header style={IconHeader} floated="right" >
                                {writePrivilege &&
                                <Button
                                    floated="right"
                                    icon
                                    labelPosition="right"
                                    primary
                                    size="small"
                                    style={FiltersOnStyle}
                                    disabled={isFormActive || !!this.state.deletingLeaseRowId}
                                    onClick={this.activateAddRequest}
                                >
                                    <Icon className="add" />
                                    Dodavanje novog zakupa
                                </Button>

                                }
                            </Header>


                        </Header>
                    </Segment>
                    {(this.state.requestMode != null) &&
                        <LeaseForm
                            lease={this.state.leaseObject}
                            onRefreshData={this.getLeases}
                            onCloseForm={this.closeForm}
                        />
                    }
                    {organizationalUnitsPrivilege &&
                        <Segment>
                            <Form>
                                <Form.Field>
                                    <label>Članica</label>
                                    <Form.Dropdown
                                        disabled={this.state.loading}
                                        search
                                        options={this.state.organizationalUnits}
                                        value={this.state.selectedOrganizationalUnit || null}
                                        selection
                                        clearable={false}
                                        placeholder={'Odaberite članicu'}
                                        onChange={(e, { value }) => this.onChangeOrganizationalUnit(value)}
                                    />
                                </Form.Field>
                            </Form>
                        </Segment>
                    }
                    <Segment>
                        {this.state.loading ?
                            <Loader active inline="centered"/>
                        :
                            <Fragment>
                                {this.state.allLeases.length ?
                                    <LeasesTable
                                        allLeases={this.state.allLeases}
                                        filteredLeases={this.state.filteredLeases}
                                        activateEditRequest={this.activateEditRequest}
                                        writePrivilege={writePrivilege}
                                        disabled={isFormActive}
                                        deletingRowId={this.state.deletingLeaseRowId}
                                        deletingSaving={this.state.deletingLeaseSaving}
                                        onDelete={this.handleLeaseDeleteClick}
                                        onDeleteConfirm={this.handleLeaseDeleteConfirm}
                                        onDeleteCancel={this.handleLeaseDeleteCancel}
                                        switchToDetails={this.switchToDetails}
                                        getSortedLeases={this.getSortedLeases}
                                        fieldName={fieldName}
                                        direction={direction}
                                        excelData={this.state.excelData}
                                        getResults={this.getResults}
                                        handleSearchTextChange={this.handleSearchTextChange}
                                        leaseSearchFields={LEASE_SEARCH_FIELDS}
                                    /> :
                                    <p>{(this.state.selectedOrganizationalUnit || !organizationalUnitsPrivilege)
                                        ? 'Nema podataka za prikaz.'
                                        : 'Nema podataka za prikaz, potrebno je odabrati članicu.'}</p>
                                }
                            </Fragment>
                        }
                    </Segment>
                    {!this.state.loading && leases.length > 0 && <PaginationSegment
                        activePage={this.state.activePage}
                        totalPages={this.state.totalPages}
                        onPageChange={this.handlePageChange}
                    />}
                    <ToastContainer hideProgressBar />
                </Segment.Group>
            </div>
        );
    }
}


export default Leases;



// WEBPACK FOOTER //
// ./src/views/Assets/Leases/Leases.js